import React, {useEffect, useState} from 'react';
import {useFormik} from 'formik';
import SearchableSingleSelectDropdown from '../../../common/SearchableDropdown';
import TextInput from '../../../common/PicklistTesxBox';
import EditableTable from './componant/PicklistItemEdit';
import Snackbar from '../../../common/Snackbar';
import NavCrumbs from '../../../common/NavCrumbs';
import useBreadcrumbs from '../../../hooks/useBreadCrumbs';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/Store';
import AccordionComponent from '../../../common/Accordion';

import {
  FetchPicklistGroups,
  FetchPicklistGroupDetails,
  FetchPicklistItems,
  UpdatePicklistGroup,
} from '../../../services/PickListApi'; // Ensure these are the correct API function imports
import {validateTeamForm} from './componant/PicklistValidations';
import {Col, Row} from 'react-bootstrap';
import MultilineTextBox from '../../../common/MultilineTextBox';

interface PicklistGroup {
  value: string;
  label: string;
}
interface GroupDetailsResponse {
  isSuccessful: boolean;
  data: {
    groupDescription: string;
    comments: string;
    isactive: number;
  };
}
interface Row {
  id: number;
  Picklistitemid: number;

  itemName: string;
  displayName: string;
  description: string;
  comments: string;
  //loggedInUserid: string;
  sortSequence: number;
  isActive: boolean;
  isEditing: boolean;
  groupId?: string;
}

const PicklistPage: React.FC = () => {
  const [isFormEnabled, setIsFormEnabled] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState<PicklistGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [picklistItems, setPicklistItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupStatus, setGroupStatus] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSaving, setIsSaving] = useState<number | null>(null); // Set to number | null
  const breadcrumbs = useBreadcrumbs();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid); // Fetch the picklist groups when the component mounts
  useEffect(() => {
    FetchPicklistGroups()
      .then(async (response) => {
        // console.log("Fetched picklist groups:", response); // Log the response

        // Check if the response is directly an array
        if (Array.isArray(response)) {
          const options = response.map((item: any) => ({
            value: item.value.toString(),
            label: item.label.toString(),
          }));
          //console.log("Mapped options:", options);
          setDropdownOptions(options);
        } else {
          console.error(
            'Invalid response structure: Expected an array',
            response,
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching picklist groups:', error);
        setSnackbarMessage('Error fetching picklist groups.');
        setSnackbarOpen(true);
      });
  }, []);

  const formik = useFormik({
    initialValues: {comment: '', description: '', dropdown: ''},
    onSubmit: (values) => {
      if (validateForm()) {
        setIsSaving(1); // Set to 1 when saving starts
        UpdatePicklistGroup(
          selectedGroupId as string,
          values.description,
          values.comment,
          loggedInUserid ?? 0, // Replace with actual logged-in user ID if dynamic
        )
          .then((data) => {
            setIsSaving(0); // Set to 0 when saving completes

            // Check for the expected response structure
            if (data && data.isSuccess) {
              // Success response
              setSnackbarMessage('Picklist group updated successfully!');
              setSnackbarOpen(true);
              setpicklistformKey(null);
            } else {
              // If API response message is wrong or failure case
              setSnackbarMessage(
                data?.message || 'Failed to update picklist group.',
              );
              setSnackbarOpen(true);
            }
          })
          .catch((error) => {
            setIsSaving(0); // Set to 0 when saving completes or fails
            // Handle error more specifically
            console.error('Error updating picklist group:', error);
            setSnackbarMessage('Error updating picklist group.');
            setSnackbarOpen(true);
          });
      }
    },
  });

  const fetchDetails = (groupId: string) => {
    //console.log("Fetching details for Group ID:", groupId);
    setLoading(true);

    FetchPicklistGroupDetails(groupId)
      .then((data) => {
        // console.log("Data received:", data);

        if (data.isSuccess) {
          // Check if the API response is successful
          const groupDetails = data.result; // Use 'result' as per your response structure
          formik.setFieldValue(
            'description',
            groupDetails.groupDescription || '',
          );
          formik.setFieldValue('comment', groupDetails.comments || '');
          setGroupStatus(groupDetails.isactive === 1 ? 'Active' : 'Inactive');
          setIsFormEnabled(true); // Enable form once details are fetched
          fetchPicklistItems(Number(groupId));
        } else {
          throw new Error('API returned an unsuccessful response.'); // Handle unsuccessful API response
        }
      })
      .catch((error) => {
        console.error('Error fetching group details:', error);
        // Log the entire error to get more insight
        setSnackbarMessage(
          `Error fetching group details: ${error.message || error}`,
        );
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  };

  const fetchPicklistItems = (groupId: number) => {
    FetchPicklistItems(groupId)
      .then((items) => {
        const formattedItems = items.map((item: any) => ({
          id: item.pickListItemId, // Updated here
          pickListItemId: item.pickListItemId,
          itemName: item.itemName,
          displayName: item.displayName,
          description: item.itemdescription,
          comments: item.itemComments,
          sortSequence: item.sortSequence,
          isActive: item.isactive === 1,
          createdbyName: item.createdbyName,
          createddate: item.createddate,
          modifiedby: item.modifiedby,
          modifiedbyName: item.modifiedbyName,
          modifieddate: item.modifieddate,
          loggedInUserId: loggedInUserid,
        }));

        setPicklistItems(formattedItems);
      })
      .catch((error) => {
        setSnackbarMessage('Error fetching picklist items.');
        setSnackbarOpen(true);
      });
  };

  const validateForm = () => {
    const errors = validateTeamForm(
      selectedGroupId,
      formik.values.comment,
      formik.values.description,
    );
    if (errors.length > 0) {
      setSnackbarMessage(errors.join('\n'));
      setSnackbarOpen(true);
      return false;
    }
    return true;
  };

  const handleCancel = () => {
    formik.resetForm(); // Reset form fields
    setSelectedGroupId(null); // Clear selected group
    setGroupStatus(null); // Reset status
    setPicklistItems([]); // Clear picklist items

    setIsFormEnabled(false);
  };

  const handleGroupChange = (selectedOption: any) => {
    const groupId = selectedOption?.value || null;
    setSelectedGroupId(groupId);
    setIsFormEnabled(!!groupId); // Enable form only when a group is selected
    formik.setFieldValue('dropdown', groupId);

    if (groupId) {
      fetchDetails(groupId);
    }
  };

  //Picklist Accordion Added

  const selectedGroupLabel =
    dropdownOptions.find((option) => option.value === selectedGroupId)?.label ||
    '';

  const formattedLabel = selectedGroupLabel.replaceAll('_', ' ');

  const [picklistformKey, setpicklistformKey] = useState<string | null>('0');

  const picklistformAccordion = (
    items: {title: string; content: JSX.Element}[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
        title:
          picklistformKey === index.toString() ? (
            item.title
          ) : (
            <>
              {item.title}
              <span className='fw-normal'>
                {formattedLabel ? ` - ${formattedLabel}` : ''}
              </span>
            </>
          ),
      }))}
      activeKey={picklistformKey}
      onSelect={(key) => setpicklistformKey(key as string)}
    />
  );

  const [picklistKey, setpicklistKey] = useState<string | null>('0');

  const picklistAccordion = (
    items: {title: string; content: JSX.Element}[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={picklistKey}
      onSelect={(key) => setpicklistKey(key as string)}
    />
  );

  useEffect(() => {
    const handleScroll = () => {
      const formBottom = document
        .getElementById('team-form-buttons')
        ?.getBoundingClientRect().bottom;
      const distanceFromBottom = window.innerHeight - (formBottom ?? 0);

      // Only collapse if buttons are MORE THAN 100px from the bottom
      if (picklistformKey !== null && distanceFromBottom > 100) {
        setpicklistformKey(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [picklistformKey]);

  //Picklist Accordion Added Section Ends Here

  return (
    <div>
      <div className='breadcrumbg'>
        <div className='container-fluid px-5'>
          {/* Heading */}
          <div className='pt-0 pb-0 fade-in-left'>
            {/* Heading */}
            <div className='d-flex justify-content-between align-items-center'>
              <h4>Picklist Groups</h4>
            </div>

            {/* Breadcrumbs and Button */}
            <div className='d-flex justify-content-between align-items-center'>
              <div className='d-flex justify-content-start'>
                <NavCrumbs breadcrumbs={breadcrumbs} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='main_wrapper_section'>
        <Snackbar
          open={snackbarOpen}
          message={snackbarMessage}
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes('successfully') ? 'success' : 'error'
          } // Green for success, red for error
        />
        <div className='container-fluid px-5 mt-3'>
          {picklistformAccordion([
            {
              title: 'Group',
              content: (
                <>
                  <form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col
                        md={3}
                        className='selection_dropdown mb-2 adj_label_lt'
                      >
                        <SearchableSingleSelectDropdown
                          label='Picklist Group'
                          name='dropdown'
                          options={dropdownOptions}
                          value={formik.values.dropdown}
                          onChange={(event, name, value) => {
                            if (
                              typeof value === 'string' &&
                              value.length <= 50
                            ) {
                              formik.setFieldValue('dropdown', value);
                              setSelectedGroupId(value);
                              fetchDetails(value); // Fetch group details when the selection changes
                            }
                          }}
                          required
                        />
                      </Col>

                      {selectedGroupId && (
                        <div className='col-md-3'>
                          <p className='mb-2'>Status</p>
                          <span
                            className={`p-1 px-4 ${
                              groupStatus === 'Active'
                                ? 'activeinactive'
                                : 'inactive'
                            }`}
                          >
                            {groupStatus}
                          </span>
                        </div>
                      )}
                    </Row>
                    <Row>
                      <Col md={6} className='mb-3'>
                        <MultilineTextBox
                          label='Description'
                          name='description'
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          maxLength={100}
                          required
                          disabled={!selectedGroupId}
                        />
                      </Col>
                      <Col md={6} className='mb-3'>
                        <MultilineTextBox
                          label='Comments'
                          name='comment'
                          value={formik.values.comment} // ✅ Ensure it's controlled
                          onChange={formik.handleChange} // ✅ Formik's handler                  required
                          maxLength={1000}
                          required
                          disabled={!selectedGroupId}
                        />
                      </Col>
                    </Row>

                    <hr style={{marginTop: '6px'}} />
                    <div className='row '>
                      <div className='col-md-12 d-flex justify-content-end'>
                        <button
                          type='button'
                          className='btn d-flex align-items-center btn-text'
                          onClick={handleCancel}
                          disabled={!isFormEnabled} // Ensure this is controlled by state
                        >
                          <span className='material-icons mx-1'>close</span>
                          Cancel
                        </button>
                        <button
                          type='submit'
                          className='btn btn-primary ml-3 d-flex align-items-center'
                          disabled={!isFormEnabled} // Ensure this is controlled by state
                        >
                          <span className='material-icons mx-1'>save</span>Save
                        </button>
                      </div>
                    </div>
                  </form>
                </>
              ),
            },
          ])}

          {selectedGroupId &&
            picklistAccordion([
              {
                title: 'List',
                content: loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className='picklist_gridWrapper'>
                    <EditableTable
                      rows={picklistItems}
                      setRows={setPicklistItems}
                      isSaving={isSaving}
                      setIsSaving={setIsSaving}
                      selectedGroupId={selectedGroupId}
                      fetchPicklistItems={fetchPicklistItems}
                    />
                  </div>
                ),
              },
            ])}
        </div>
      </div>
    </div>
  );
};

export default PicklistPage;
